<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import Dropdown from "primevue/dropdown";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Scheduler from "../../../Components/Management/Scheduler.vue";
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'flash',
]);

const route = window.route;
const toast = useToast();
const errors = ref({});
const activeTab = ref(0);

const timezones = [
    { label: 'Europe/Zurich', value: 'Europe/Zurich' },
    { label: 'Europe/Berlin', value: 'Europe/Berlin' },
    { label: 'Europe/Vienna', value: 'Europe/Vienna' },
    { label: 'Europe/London', value: 'Europe/London' },
];

const form = useForm({
    name: '',
    email: '',
    phone: '',
    mobile: '',
    street_name: '',
    postcode: '',
    city: '',
    country: 'Schweiz',
    domain: '',
    foundation: null,
    description: '',
    timezone: 'Europe/Zurich',
    is_active: true,
    working_hours: [
        { day_of_week: 1, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 2, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 3, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 4, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 5, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 6, is_workday: true, start_time: '10:00', end_time: '18:00' },
        { day_of_week: 0, is_workday: false, start_time: null, end_time: null },
    ],
});

const validateTab = (tabIndex) => {
    errors.value = {};

    if (tabIndex === 0) {
        if (!form.name) errors.value.name = 'Name des Unternehmens ist erforderlich';
        if (!form.email) errors.value.email = 'E-Mail Adresse ist erforderlich';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errors.value.email = 'Ungültiges E-Mail-Format';
        }
        if (!form.foundation) errors.value.foundation = 'Gründungsdatum ist erforderlich';
        if (!form.street_name) errors.value.street_name = 'Strasse & Nr. ist erforderlich';
        if (!form.postcode) errors.value.postcode = 'PLZ ist erforderlich';
        if (!form.city) errors.value.city = 'Stadt ist erforderlich';
        if (!form.phone) errors.value.phone = 'Telefon ist erforderlich';
    }

    if (tabIndex === 1) {
        form.working_hours.forEach((h, idx) => {
            if (h.is_workday) {
                if (!h.start_time) errors.value[`working_hours.${idx}.start_time`] = 'Beginn ist erforderlich';
                if (!h.end_time) errors.value[`working_hours.${idx}.end_time`] = 'Ende ist erforderlich';
                if (h.start_time && h.end_time && h.start_time >= h.end_time) {
                    errors.value[`working_hours.${idx}.end_time`] = 'Ende muss nach Beginn liegen';
                }
            }
        });
    }
    
    return Object.keys(errors.value).length === 0;
};

const nextTab = () => {
    if (validateTab(activeTab.value)) {
        activeTab.value++;
    }
};

const prevTab = () => {
    activeTab.value--;
};

const submit = () => {
    // Validate all tabs before final submission
    if (!validateTab(0)) {
        activeTab.value = 0;
        return;
    }

    form.post('/management/companies', {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Unternehmen wurde erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            
            // Auto-switch tabs if there are errors on the first tab
            const tab0Fields = ['name', 'email', 'foundation', 'street_name', 'postcode', 'city', 'phone'];
            if (Object.keys(err).some(key => tab0Fields.includes(key))) {
                activeTab.value = 0;
            }

            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben in den markierten Feldern.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Unternehmen erstellen" />

        <div class="text-sm">
            <h1 class="text-2xl font-bold mb-8 text-gray-800">Neues Unternehmen erstellen</h1>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <div v-for="(error, key) in errors" :key="key">{{ error }}</div>
            </Message>

            <form @submit.prevent="submit" class="space-y-8" novalidate>
                <TabView v-model:activeIndex="activeTab">
                    <TabPanel header="Allgemeine Informationen">
                        <div class="space-y-8 pt-4">
                            <!-- Section 1: Basic Info -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                                    <h2 class="text-lg font-bold text-gray-700">Grundinformationen</h2>
                                </div>
                                
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <div class="flex flex-col gap-2">
                                        <label for="name" class="font-bold text-gray-600">Name des Unternehmens *</label>
                                        <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" placeholder="z.B. Vigory AG" @input="errors.name = null" />
                                        <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="email" class="font-bold text-gray-600">E-Mail Adresse *</label>
                                        <InputText id="email" type="email" v-model="form.email" :class="{'p-invalid': errors.email}" class="w-full" placeholder="info@unternehmen.ch" @input="errors.email = null" />
                                        <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="domain" class="font-bold text-gray-600">Webseite / Domain</label>
                                        <InputText id="domain" v-model="form.domain" class="w-full" placeholder="https://www.unternehmen.ch" />
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="foundation" class="font-bold text-gray-600">Gründungsdatum *</label>
                                        <Calendar id="foundation" v-model="form.foundation" dateFormat="dd.mm.yy" :class="{'p-invalid': errors.foundation}" class="w-full" inputClass="w-full" showIcon @date-select="errors.foundation = null" />
                                        <small v-if="errors.foundation" class="p-error">{{ errors.foundation }}</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Section 2: Address & Contact -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                                    <h2 class="text-lg font-bold text-gray-700">Standort & Kontakt</h2>
                                </div>
                                
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <div class="flex flex-col gap-2">
                                        <label for="street_name" class="font-bold text-gray-600">Strasse & Nr. *</label>
                                        <InputText id="street_name" v-model="form.street_name" :class="{'p-invalid': errors.street_name}" class="w-full" @input="errors.street_name = null" />
                                        <small v-if="errors.street_name" class="p-error">{{ errors.street_name }}</small>
                                    </div>

                                    <div class="grid grid-cols-3 gap-4">
                                        <div class="flex flex-col gap-2 col-span-1">
                                            <label for="postcode" class="font-bold text-gray-600">PLZ *</label>
                                            <InputText id="postcode" v-model="form.postcode" :class="{'p-invalid': errors.postcode}" class="w-full" @input="errors.postcode = null" />
                                            <small v-if="errors.postcode" class="p-error">{{ errors.postcode }}</small>
                                        </div>
                                        <div class="flex flex-col gap-2 col-span-2">
                                            <label for="city" class="font-bold text-gray-600">Stadt *</label>
                                            <InputText id="city" v-model="form.city" :class="{'p-invalid': errors.city}" class="w-full" @input="errors.city = null" />
                                            <small v-if="errors.city" class="p-error">{{ errors.city }}</small>
                                        </div>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="phone" class="font-bold text-gray-600">Telefon *</label>
                                        <InputText id="phone" v-model="form.phone" :class="{'p-invalid': errors.phone}" class="w-full" maxlength="10" @input="errors.phone = null" />
                                        <small v-if="errors.phone" class="p-error">{{ errors.phone }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="mobile" class="font-bold text-gray-600">Mobiltelefon</label>
                                        <InputText id="mobile" v-model="form.mobile" class="w-full" maxlength="10" />
                                    </div>
                                </div>
                            </div>

                            <!-- Section 3: Advanced Settings -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                                    <h2 class="text-lg font-bold text-gray-700">Erweiterte Einstellungen</h2>
                                </div>
                                
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <div class="flex flex-col gap-2">
                                        <label for="timezone" class="font-bold text-gray-600">Zeitzone</label>
                                        <Dropdown id="timezone" v-model="form.timezone" :options="timezones" optionLabel="label" optionValue="value" class="w-full" />
                                    </div>

                                    <div class="flex flex-col gap-2 justify-end">
                                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                            <Checkbox id="is_active" v-model="form.is_active" :binary="true" />
                                            <label for="is_active" class="font-bold text-gray-700 cursor-pointer">Unternehmensprofil aktiv</label>
                                        </div>
                                    </div>

                                    <div class="flex flex-col gap-2 md:col-span-2">
                                        <label for="description" class="font-bold text-gray-600">Interne Beschreibung</label>
                                        <Textarea id="description" v-model="form.description" rows="4" class="w-full" placeholder="Zusätzliche Informationen zum Unternehmen..." />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="Öffnungszeiten">
                        <div class="pt-4">
                            <Scheduler v-model="form.working_hours" title="Wöchentliche Arbeitszeiten" :errors="errors" @clear-error="(key) => errors[key] = null" />
                        </div>
                    </TabPanel>
                </TabView>

                <!-- Footer Actions -->
                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <!-- Left: Always Abbrechen -->
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/companies" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            <i class="pi pi-times mr-2" />
                            Abbrechen
                        </Link>
                    </Button>

                    <!-- Right: Navigation & Submit -->
                    <div class="flex items-center gap-4">
                        <Button v-if="activeTab > 0" type="button" label="Zurück" icon="pi pi-arrow-left" severity="secondary" text @click="prevTab" />
                        
                        <Button v-if="activeTab < 1" type="button" 
                                label="Weiter zu Öffnungszeiten" 
                                icon="pi pi-arrow-right" iconPos="right" 
                                class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-sm" 
                                @click="nextTab" />
                        
                        <Button v-if="activeTab === 1" type="submit" 
                                class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                                :loading="form.processing" :disabled="form.processing">
                            <i class="pi pi-save" />
                            Unternehmen speichern
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    </Layout>
</template>
